/**
 * Geofence boundary editor map — MapLibre only inside pmv/map abstraction.
 * Circle: click to set center. Polygon: click vertices, Finish to close.
 */

import React, { useEffect, useRef, useState } from 'react';
import maplibregl, { type Map, type GeoJSONSource, type Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Button, InputNumber, Space, Typography } from 'antd';
import { circleToPolygon, getFleetMapStyleUrl } from './mapConfig';

const { Text } = Typography;

const SOURCE = 'pmv-geofence-draft';
const FILL = 'pmv-geofence-draft-fill';
const LINE = 'pmv-geofence-draft-line';

export type GeofenceDraftGeometry =
  | {
      geometry_type: 'CIRCLE';
      center_lat: number;
      center_lon: number;
      radius_m: number;
    }
  | {
      geometry_type: 'POLYGON';
      geometry_json: { type: 'Polygon'; coordinates: number[][][] };
    };

export type GeofenceEditorMapProps = {
  geometryType: 'CIRCLE' | 'POLYGON';
  value?: GeofenceDraftGeometry | null;
  onChange?: (g: GeofenceDraftGeometry | null) => void;
  height?: number | string;
  readOnly?: boolean;
};

function draftToGeoJSON(
  geometryType: 'CIRCLE' | 'POLYGON',
  value: GeofenceDraftGeometry | null | undefined,
  polyVerts: [number, number][]
): GeoJSON.FeatureCollection {
  if (geometryType === 'CIRCLE' && value?.geometry_type === 'CIRCLE') {
    const f = circleToPolygon(value.center_lat, value.center_lon, value.radius_m);
    return { type: 'FeatureCollection', features: [f] };
  }
  if (geometryType === 'POLYGON') {
    const ring =
      value?.geometry_type === 'POLYGON'
        ? value.geometry_json.coordinates[0]
        : polyVerts.length
          ? [...polyVerts.map(([lon, lat]) => [lon, lat]), ...(polyVerts.length >= 3 ? [[polyVerts[0][0], polyVerts[0][1]]] : [])]
          : [];
    if (ring.length >= 4) {
      return {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: { type: 'Polygon', coordinates: [ring] },
          },
        ],
      };
    }
  }
  return { type: 'FeatureCollection', features: [] };
}

export const GeofenceEditorMap: React.FC<GeofenceEditorMapProps> = ({
  geometryType,
  value,
  onChange,
  height = 360,
  readOnly = false,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const markerRef = useRef<Marker | null>(null);
  const [ready, setReady] = useState(false);
  const [polyVerts, setPolyVerts] = useState<[number, number][]>([]);
  const [radius, setRadius] = useState(
    value?.geometry_type === 'CIRCLE' ? value.radius_m : 200
  );
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;
  const polyRef = useRef(polyVerts);
  polyRef.current = polyVerts;
  const radiusRef = useRef(radius);
  radiusRef.current = radius;
  const typeRef = useRef(geometryType);
  typeRef.current = geometryType;

  useEffect(() => {
    if (value?.geometry_type === 'CIRCLE') setRadius(value.radius_m);
    if (value?.geometry_type === 'POLYGON') {
      const ring = value.geometry_json.coordinates[0] || [];
      const verts = ring.slice(0, -1).map((c) => [c[0], c[1]] as [number, number]);
      setPolyVerts(verts);
    }
  }, [value]);

  useEffect(() => {
    if (!containerRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: getFleetMapStyleUrl(false),
      center:
        value?.geometry_type === 'CIRCLE'
          ? [value.center_lon, value.center_lat]
          : [54.37, 24.45],
      zoom: 12,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;

    map.on('load', () => {
      map.addSource(SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: FILL,
        type: 'fill',
        source: SOURCE,
        paint: { 'fill-color': '#2563eb', 'fill-opacity': 0.15 },
      });
      map.addLayer({
        id: LINE,
        type: 'line',
        source: SOURCE,
        paint: { 'line-color': '#1d4ed8', 'line-width': 2 },
      });
      setReady(true);
    });

    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (readOnly) return;
      const { lng, lat } = e.lngLat;
      if (typeRef.current === 'CIRCLE') {
        const next: GeofenceDraftGeometry = {
          geometry_type: 'CIRCLE',
          center_lat: lat,
          center_lon: lng,
          radius_m: radiusRef.current,
        };
        onChangeRef.current?.(next);
        if (markerRef.current) markerRef.current.setLngLat([lng, lat]);
        else {
          markerRef.current = new maplibregl.Marker({ color: '#1d4ed8' })
            .setLngLat([lng, lat])
            .addTo(map);
        }
      } else {
        const nextVerts: [number, number][] = [...polyRef.current, [lng, lat]];
        setPolyVerts(nextVerts);
        if (nextVerts.length >= 3) {
          const ring = [...nextVerts.map(([lon, la]) => [lon, la]), [nextVerts[0][0], nextVerts[0][1]]];
          onChangeRef.current?.({
            geometry_type: 'POLYGON',
            geometry_json: { type: 'Polygon', coordinates: [ring] },
          });
        } else {
          onChangeRef.current?.(null);
        }
      }
    };
    map.on('click', onClick);

    return () => {
      markerRef.current?.remove();
      markerRef.current = null;
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !map.getSource(SOURCE)) return;
    const data = draftToGeoJSON(geometryType, value, polyVerts);
    (map.getSource(SOURCE) as GeoJSONSource).setData(data);

    if (geometryType === 'CIRCLE' && value?.geometry_type === 'CIRCLE') {
      if (!markerRef.current) {
        markerRef.current = new maplibregl.Marker({ color: '#1d4ed8' })
          .setLngLat([value.center_lon, value.center_lat])
          .addTo(map);
      } else {
        markerRef.current.setLngLat([value.center_lon, value.center_lat]);
      }
    }
  }, [value, polyVerts, geometryType, ready]);

  const finishPolygon = () => {
    if (polyVerts.length < 3) return;
    const ring = [...polyVerts.map(([lon, lat]) => [lon, lat]), [polyVerts[0][0], polyVerts[0][1]]];
    onChange?.({
      geometry_type: 'POLYGON',
      geometry_json: { type: 'Polygon', coordinates: [ring] },
    });
  };

  const clear = () => {
    setPolyVerts([]);
    markerRef.current?.remove();
    markerRef.current = null;
    onChange?.(null);
  };

  const fit = () => {
    const map = mapRef.current;
    if (!map) return;
    if (value?.geometry_type === 'CIRCLE') {
      const f = circleToPolygon(value.center_lat, value.center_lon, value.radius_m);
      const b = new maplibregl.LngLatBounds();
      for (const [lon, lat] of f.geometry.coordinates[0]) b.extend([lon, lat]);
      map.fitBounds(b, { padding: 40, duration: 400 });
    } else if (polyVerts.length) {
      const b = new maplibregl.LngLatBounds();
      for (const [lon, lat] of polyVerts) b.extend([lon, lat]);
      map.fitBounds(b, { padding: 40, duration: 400 });
    }
  };

  return (
    <div>
      {!readOnly ? (
        <Space wrap style={{ marginBottom: 8 }}>
          {geometryType === 'CIRCLE' ? (
            <>
              <Text type="secondary">Click map to set center</Text>
              <Text>Radius (m)</Text>
              <InputNumber
                min={10}
                max={50000}
                value={radius}
                onChange={(v) => {
                  const r = Number(v) || 200;
                  setRadius(r);
                  if (value?.geometry_type === 'CIRCLE') {
                    onChange?.({ ...value, radius_m: r });
                  }
                }}
              />
            </>
          ) : (
            <>
              <Text type="secondary">Click to add vertices ({polyVerts.length})</Text>
              <Button size="small" onClick={finishPolygon} disabled={polyVerts.length < 3}>
                Finish polygon
              </Button>
            </>
          )}
          <Button size="small" onClick={fit}>
            Fit boundary
          </Button>
          <Button size="small" danger onClick={clear}>
            Clear
          </Button>
        </Space>
      ) : null}
      <div
        ref={containerRef}
        role="application"
        aria-label={readOnly ? 'Geofence boundary map' : 'Geofence boundary editor'}
        tabIndex={0}
        style={{
          height,
          width: '100%',
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid #e5e5e5',
        }}
      />
    </div>
  );
};

export default GeofenceEditorMap;
